import { Sparkles, ClipboardList, Coins } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WizardRateMode } from "./RateTableEditor";

/**
 * Three-segment rate model toggle. Mirrors the segmented control pattern
 * used elsewhere in the app (Admin → States tab bar).
 *
 * Per benefit amount (was "Per $1,000") — benefit unit varies by carrier
 * (some life rate per $1k face value, some per $10k, etc.). The label is
 * carrier-friendly even though the storage column is `volume_per_thousand`.
 */

const SEGMENTS: Array<{
  value: WizardRateMode;
  label: string;
  icon: React.ReactNode;
}> = [
  { value: "age_tier_rates", label: "Age rated", icon: <Sparkles className="h-3.5 w-3.5" /> },
  { value: "tier_rates", label: "Tier rates", icon: <ClipboardList className="h-3.5 w-3.5" /> },
  { value: "volume_per_thousand", label: "Per benefit amount", icon: <Coins className="h-3.5 w-3.5" /> },
];

interface Props {
  value: WizardRateMode;
  onChange: (v: WizardRateMode) => void;
  disabled?: boolean;
}

export function RateModelToggle({ value, onChange, disabled }: Props) {
  return (
    <div
      role="tablist"
      className="inline-flex gap-[2px] p-[3px] bg-stone-100 border border-stone-200 rounded-[9px] w-fit"
    >
      {SEGMENTS.map((seg) => {
        const selected = value === seg.value;
        return (
          <button
            key={seg.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => !disabled && onChange(seg.value)}
            disabled={disabled}
            data-testid={`rate-model-${seg.value}`}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] text-[12.5px] font-medium transition-colors",
              selected
                ? "bg-white text-stone-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                : "text-stone-500 hover:text-stone-900",
              disabled && "opacity-60 cursor-not-allowed"
            )}
          >
            <span className={selected ? "text-blue-700" : "text-stone-400"}>{seg.icon}</span>
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}
